import type { FC, ReactNode } from 'react'
import type { PublicPageProps } from '@/types'

import { useI18n } from '@/i18n'
import { SiteHeader } from '@/components/site/site-header'
import { SiteFooter } from '@/components/site/site-footer'

interface SiteLayoutProps extends PublicPageProps {
    transparentHeader?: boolean
    children: ReactNode
}

export const SiteLayout: FC<SiteLayoutProps> = ({ settings, viewer, today, transparentHeader = false, children }): ReactNode => {
    const { t } = useI18n()

    return (
        <div className='flex min-h-screen flex-col bg-background'>
            <a
                href='#main'
                className='sr-only z-50 rounded-full bg-navy px-4 py-2 text-sm font-semibold text-white focus:not-sr-only focus:fixed focus:start-4 focus:top-4'
            >
                {t.nav.skip}
            </a>

            <SiteHeader viewer={viewer} transparent={transparentHeader} />

            <main id='main' className='flex-1'>
                {children}
            </main>

            <SiteFooter settings={settings} viewer={viewer} today={today} />
        </div>
    )
}

interface PageIntroProps {
    eyebrow?: ReactNode
    title: ReactNode
    text?: ReactNode
    children?: ReactNode
}

export const PageIntro: FC<PageIntroProps> = ({ eyebrow, title, text, children }): ReactNode => (
    <section className='relative overflow-hidden border-b border-border bg-sand'>
        <div aria-hidden='true' className='pointer-events-none absolute -end-24 -top-24 size-80 rounded-full bg-gold/10 blur-3xl' />
        <div className='relative mx-auto max-w-7xl px-4 py-14 sm:px-6 md:py-20 lg:px-8'>
            {eyebrow && <p className='text-xs font-bold tracking-[0.25em] text-gold uppercase'>{eyebrow}</p>}
            <h1 className='mt-3 max-w-3xl text-4xl leading-tight font-semibold text-navy md:text-5xl'>{title}</h1>
            {text && <p className='mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg'>{text}</p>}
            {children}
        </div>
    </section>
)

interface SectionHeadingProps {
    eyebrow?: ReactNode
    title: ReactNode
    text?: ReactNode
    align?: 'start' | 'center'
    tone?: 'dark' | 'light'
}

export const SectionHeading: FC<SectionHeadingProps> = ({ eyebrow, title, text, align = 'start', tone = 'dark' }): ReactNode => (
    <div className={align === 'center' ? 'mx-auto max-w-2xl text-center' : 'max-w-2xl'}>
        {eyebrow && <p className='text-xs font-bold tracking-[0.25em] text-gold uppercase'>{eyebrow}</p>}
        <h2 className={`mt-3 text-3xl leading-tight font-semibold md:text-[2.75rem] ${tone === 'light' ? 'text-white' : 'text-navy'}`}>{title}</h2>
        {text && <p className={`mt-4 text-base leading-relaxed md:text-lg ${tone === 'light' ? 'text-white/80' : 'text-muted-foreground'}`}>{text}</p>}
    </div>
)
